import { ComponentActivity } from '../component.constants';
import {
  LEARNING_SEQUENCE_CATEGORY_EXPLANATION,
  LEARNING_SEQUENCE_ITEM_TYPE_QUESTION,
  LEARNING_SEQUENCE_CATEGORY_LEVEL_OF_ATTAINMENT,
  LearningSequenceItemTypes,
  LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED,
  LEARNING_SEQUENCE_CATEGORY_EXTRA_CONNECTIONS,
  LEARNING_SEQUENCE_ITEM_TYPE_INTRODUCTION,
  LEARNING_SEQUENCE_CATEGORY_INTENTION,
  LEARNING_SEQUENCE_CATEGORY_ACTIVITY_DESCRIPTION,
} from 'global/constants';
import { ACTIVITY_ID_PREFIX } from '../activity/activity.constants';
import type { GetLessonTeachingAndLearningSequenceQuery } from './teaching-notes.graphql.generated';
import type { LessonActivitiesFragment } from '../../lesson-builder.graphql.generated';
import { SectionOrSubSectionOverview } from 'approot/activity-builder/activity-builder.constants';
import type { GetDraftLessonActivitiesQuery } from '../../lesson-builder.graphql.generated';
import type { LessonMaterialInput } from '../../../../gql/schema-types';
import type { Pluck } from 'lib/gql/pluck';

export function getTotalDuration(activities: ComponentActivity[]) {
  return activities.reduce((total, curr) => {
    return total + (curr.properties.activitySettings.duration || 0);
  }, 0);
}

export function getExplanationNote(
  sequences:
    | Pluck<
        GetLessonTeachingAndLearningSequenceQuery,
        'getLessonTeachingAndLearningSequence'
      >[]
    | null,
  componentId: string
) {
  return sequences?.find(
    s =>
      s.type === LEARNING_SEQUENCE_ITEM_TYPE_QUESTION &&
      s.category === LEARNING_SEQUENCE_CATEGORY_EXPLANATION &&
      s.component?.id === componentId
  );
}

export function getLevelOfAttainmentNote(
  sequences:
    | Pluck<
        GetLessonTeachingAndLearningSequenceQuery,
        'getLessonTeachingAndLearningSequence'
      >[]
    | null,
  componentId: string
) {
  return sequences?.find(
    s =>
      s.category === LEARNING_SEQUENCE_CATEGORY_LEVEL_OF_ATTAINMENT &&
      s.component?.id === componentId
  );
}

export function getLessonMaterialsNote(
  sequences:
    | Pluck<
        GetLessonTeachingAndLearningSequenceQuery,
        'getLessonTeachingAndLearningSequence'
      >[]
    | null,
  componentId: string
) {
  return sequences?.find(
    s =>
      s.category === LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED &&
      s.component?.id === componentId
  );
}

export function getExtraConnectionsNote(
  sequences:
    | Pluck<
        GetLessonTeachingAndLearningSequenceQuery,
        'getLessonTeachingAndLearningSequence'
      >[]
    | null,
  componentId: string
) {
  return sequences?.find(
    s =>
      s.category === LEARNING_SEQUENCE_CATEGORY_EXTRA_CONNECTIONS &&
      s.component?.id === componentId
  );
}

export function getNotes(
  sequences:
    | Pluck<
        GetLessonTeachingAndLearningSequenceQuery,
        'getLessonTeachingAndLearningSequence'
      >[]
    | null,
  componentId: string | undefined,
  itemType: LearningSequenceItemTypes
) {
  return sequences?.filter(
    s =>
      s.type === itemType &&
      s.category !== LEARNING_SEQUENCE_CATEGORY_EXPLANATION &&
      s.category !== LEARNING_SEQUENCE_CATEGORY_LEVEL_OF_ATTAINMENT &&
      s.category !== LEARNING_SEQUENCE_CATEGORY_ACTIVITY_DESCRIPTION &&
      (s.component && componentId ? s.component.id === componentId : true)
  );
}

export function getAllExplanations(
  sequences:
    | Pluck<
        GetLessonTeachingAndLearningSequenceQuery,
        'getLessonTeachingAndLearningSequence'
      >[]
    | null
) {
  return sequences
    ?.filter(
      s =>
        s.type === LEARNING_SEQUENCE_ITEM_TYPE_QUESTION &&
        (s.category === LEARNING_SEQUENCE_CATEGORY_EXPLANATION ||
          s.category === LEARNING_SEQUENCE_CATEGORY_ACTIVITY_DESCRIPTION)
    )
    .map(s => s.component as ComponentActivity);
}

export function scrollQuestionIntoView(questionId: string) {
  setTimeout(() => {
    const questionElement = document.getElementById(
      `${ACTIVITY_ID_PREFIX}${questionId}`
    );

    questionElement?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });
  }, 800);
}

export function getParentSectionOverviewIndex({
  sectionsOverview,
  sectionsAndSubSectionsOverview,
  index,
}: {
  sectionsOverview: Pluck<
    GetDraftLessonActivitiesQuery,
    'getDraftLessonActivities.sectionsOverview'
  >[];
  sectionsAndSubSectionsOverview: SectionOrSubSectionOverview[];
  index: number;
}) {
  const currentSectionOverview = sectionsAndSubSectionsOverview[index] as
    | Pluck<LessonActivitiesFragment, 'sectionsOverview.subSectionsOverview'>
    | undefined;
  if (!currentSectionOverview) return -1;

  return sectionsOverview.findIndex(s =>
    currentSectionOverview.parentSectionId
      ? s.id === currentSectionOverview.parentSectionId
      : s.id === currentSectionOverview.id
  );
}

export function getLessonMaterialPayload(
  materials:
    | Pluck<
        GetLessonTeachingAndLearningSequenceQuery,
        'getLessonTeachingAndLearningSequence.lessonMaterials'
      >[]
    | null
): LessonMaterialInput[] | null {
  return (
    // remove unnecessary fields to match expected input
    materials?.map(material => {
      const {
        alternativeMaterials,
        unitsOfMeasurement,
        isReusable,
        isStandardised,
        normalisedNumberOfItems,
        __typename,
        ...rest
      } = material;

      return {
        ...rest,
        alternativeMaterialIds: alternativeMaterials?.map(am => am.id),
      };
    }) || null
  );
}

export function getIntroductionNotes(
  sequences:
    | Pluck<
        GetLessonTeachingAndLearningSequenceQuery,
        'getLessonTeachingAndLearningSequence'
      >[]
    | null
) {
  const allNotes = getNotes(
    sequences,
    undefined,
    LEARNING_SEQUENCE_ITEM_TYPE_INTRODUCTION
  );

  // we don't display this "default" learning intention anymore
  // but existing data could exist so we need to filter it out
  const learningIntention = allNotes?.find(
    note =>
      note.category === LEARNING_SEQUENCE_CATEGORY_INTENTION &&
      note.sort === null
  );

  return allNotes?.filter(note => note.id !== learningIntention?.id);
}
